import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PaymentGroup } from '@/types/paymentConfirmation';
import { formatCurrency } from '@/utils/paymentUtils';
import { CheckCircle, Clock, ArrowRight, Info } from 'lucide-react';
import { useTranslations } from 'use-intl';

interface PaymentOverviewStepProps {
  paymentGroups: PaymentGroup[];
  onStartSigning: (groupId: string) => void;
}

const PaymentOverviewStep: React.FC<PaymentOverviewStepProps> = ({
  paymentGroups,
  onStartSigning,
}) => {
  const t = useTranslations('consultantPayments.confirm.overview');
  const getStatusIcon = (status: PaymentGroup['status']) => {
    switch (status) {
      case 'signed':
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'signing':
        return <Clock className="h-4 w-4 text-blue-600 animate-pulse" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: PaymentGroup['status']) => {
    switch (status) {
      case 'signed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">{t('badges.signed')}</Badge>;
      case 'completed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">{t('badges.completed')}</Badge>;
      case 'signing':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">{t('badges.signing')}</Badge>;
      default:
        return <Badge variant="outline">{t('badges.pending')}</Badge>;
    }
  };

  const hasSplitGroups = paymentGroups.some(g => g.splitInfo);
  const nextPendingGroup = paymentGroups.find(group => group.status === 'pending');

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">{t('title')}</h3>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      {hasSplitGroups && (
        <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{t('splitNote')}</span>
        </div>
      )}

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {paymentGroups.map((group) => {
          const isPendingGroup = group.status === 'pending';
          return (
            <Card key={group.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">
                      {group.bankName || group.bankAccount}
                    </CardTitle>
                    {group.splitInfo && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('part', { part: String(group.splitInfo.partIndex), total: String(group.splitInfo.totalParts), limit: String(group.splitInfo.bankLimit) })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(group.status)}
                    {getStatusBadge(group.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-1">
                      {group.totalsByCurrency && Object.entries(group.totalsByCurrency).map(([currency, amount]) => (
                        <span key={currency} className="text-sm font-semibold bg-muted px-2 py-0.5 rounded">
                          {formatCurrency(amount, currency)}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {t('payments', { count: group.invoices.length })}
                    </p>
                    {group.sourceIbans && group.sourceIbans.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-2">
                        <span className="font-medium">{t('sourceIbans')}</span>{' '}
                        {group.sourceIbans.join(', ')}
                      </div>
                    )}
                  </div>
                  {isPendingGroup && (
                    <Button
                      onClick={() => onStartSigning(group.id)}
                      className="flex items-center gap-2 ml-4"
                    >
                      {t('sign', { count: group.invoices.length })}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {!nextPendingGroup && (
        <div className="text-center py-4">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('allProcessed')}</p>
        </div>
      )}
    </div>
  );
};

export default PaymentOverviewStep;
